import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, MessageSquare, RefreshCw, SendHorizonal } from 'lucide-react';
import { useToast } from '@/lib/toast';
import { useRenderMode } from '@/hooks/useRenderMode';
import { useEntityDiscussionThread } from '@/hooks/useEntityDiscussionThread';
import { useThreadSocket } from '@/hooks/useBooxSocket';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';

interface EntityDiscussionPanelProps {
  entityType: string;
  entityId: string;
  entityLabel: string;
  entityTitle?: string;
  active: boolean;
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-SG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function initialsForName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'AI';
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('') || 'AI';
}

export function EntityDiscussionPanel({ entityType, entityId, entityLabel, entityTitle, active }: EntityDiscussionPanelProps) {
  const toast = useToast();
  const mode = useRenderMode();
  const [draft, setDraft] = useState('');
  const { thread, messages, loading, sending, error, refresh, sendMessage, appendMessage } = useEntityDiscussionThread(entityType, entityId, active);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Subscribe to live updates for this thread via WebSocket
  const { on } = useThreadSocket(thread?.id ?? null);
  useEffect(() => {
    if (!thread?.id) return;
    return on('message.new', (event) => {
      const incoming = (event as { payload: { id: string } }).payload;
      if (incoming && typeof incoming === 'object' && 'id' in incoming) {
        appendMessage(incoming as Parameters<typeof appendMessage>[0]);
        // Auto-scroll to bottom
        setTimeout(() => {
          scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
        }, 50);
      }
    });
  }, [thread?.id, on, appendMessage]);

  const title = useMemo(() => {
    if (entityTitle && entityTitle.length > 0) return entityTitle;
    return `${entityLabel} discussion`;
  }, [entityLabel, entityTitle]);

  async function handleSubmit() {
    const trimmed = draft.trim();
    if (!trimmed || sending) return;
    try {
      await sendMessage(trimmed);
      setDraft('');
    } catch (submitError) {
      toast.error(submitError instanceof Error ? submitError.message : 'Failed to send message');
    }
  }

  return (
    <Card className="border-dashed bg-muted/10">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="h-4 w-4" />
              Discussion
            </CardTitle>
            <CardDescription>
              Shared thread for {title}. Keep decisions and exceptions attached to the entity.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading || sending}>
            {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 lg:grid-cols-6">
          <div className="rounded-lg border bg-background px-3 py-2 lg:col-span-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Thread</div>
            <div className="mt-1 text-sm font-medium">{thread?.name ?? `${entityLabel} thread`}</div>
          </div>
          <div className="rounded-lg border bg-background px-3 py-2 lg:col-span-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Messages</div>
            <div className="mt-1 text-sm font-medium">{messages.length}</div>
          </div>
          <div className="rounded-lg border bg-background px-3 py-2 lg:col-span-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Scope</div>
            <div className="mt-1 text-sm font-medium">{thread?.thread_type ?? 'entity'}</div>
          </div>
        </div>

        <div className="rounded-xl border bg-background">
          <ScrollArea className={mode === 'mobile' ? 'h-[320px]' : 'h-[420px]'}>
            <div ref={scrollRef} className="space-y-4 p-4">
              {loading && messages.length === 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading discussion...
                </div>
              )}

              {!loading && messages.length === 0 && (
                <div className="rounded-lg border border-dashed px-4 py-6 text-sm text-muted-foreground">
                  No discussion yet. Start the thread with the first operational note or decision.
                </div>
              )}

              {messages.map((message) => {
                const isCurrentUser = message.sender_name === 'You';
                return (
                  <div key={message.id} className={`flex gap-3 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                    {!isCurrentUser && (
                      <Avatar className="h-8 w-8 border">
                        <AvatarFallback className="text-[11px]">{initialsForName(message.sender_name)}</AvatarFallback>
                      </Avatar>
                    )}
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-sm ${isCurrentUser ? 'bg-primary text-primary-foreground' : 'border bg-card'}`}>
                      <div className={`mb-1 flex items-center gap-2 text-[11px] ${isCurrentUser ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                        <span className="font-medium">{message.sender_name}</span>
                        <span>{formatTimestamp(message.created_at)}</span>
                      </div>
                      <div className="whitespace-pre-wrap break-words">{message.content}</div>
                    </div>
                    {isCurrentUser && (
                      <Avatar className="h-8 w-8 border">
                        <AvatarFallback className="text-[11px]">YO</AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        <Separator />

        <div className="grid gap-3 lg:grid-cols-6">
          <div className="lg:col-span-5">
            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Add an internal discussion note, handover update, or decision..."
              className="min-h-[96px] resize-y"
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                  event.preventDefault();
                  void handleSubmit();
                }
              }}
            />
          </div>
          <div className="flex flex-col justify-between gap-2 lg:col-span-1">
            <Button onClick={() => void handleSubmit()} disabled={sending || draft.trim().length === 0} className="w-full">
              {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <SendHorizonal className="mr-1.5 h-4 w-4" />}
              Send
            </Button>
            <div className="text-xs text-muted-foreground">
              Ctrl/Cmd + Enter to send
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}